/**
 * Brand asset loading for the canvas renderer (files synced by scripts/sync-brand.mjs into /brand/).
 * Everything is async and best-effort: until an image has decoded — and always in jsdom/vitest, where images never
 * load — the lookups return null and callers draw their procedural fallback. Nothing here affects game state.
 */

const BASE = `${(import.meta.env?.BASE_URL ?? '/').replace(/\/?$/, '/')}brand/`;
const canLoad = typeof Image !== 'undefined' && typeof document !== 'undefined';

interface ImgEntry {
  img: HTMLImageElement;
  ready: boolean;
}
const images = new Map<string, ImgEntry>();

/** The decoded image at `/brand/<path>`, or null while it loads (or if it failed). Starts loading on first call. */
export function brandImage(path: string): HTMLImageElement | null {
  let e = images.get(path);
  if (!e) {
    if (!canLoad) return null;
    const img = new Image();
    e = { img, ready: false };
    images.set(path, e);
    const entry = e;
    img.onload = () => {
      // decode() keeps the first draw from stalling a frame; failure to decode just leaves the fallback in place.
      (img.decode ? img.decode() : Promise.resolve()).then(
        () => (entry.ready = img.naturalWidth > 0),
        () => (entry.ready = img.naturalWidth > 0),
      );
    };
    img.src = BASE + path;
  }
  return e.ready ? e.img : null;
}

// ---- vector art rasterised per device size ---------------------------------------------------------------------
// SVG world art is drawn into an offscreen canvas once per (file, device-pixel size) so it is crisp at any DPR and
// cheap to blit every frame. Sizes are bucketed to keep the cache small.
const rasters = new Map<string, HTMLCanvasElement>();
const BUCKET = 8;

/**
 * A raster of the square SVG at `/brand/<path>` at least `devicePx` pixels wide, or null if not loaded yet.
 * Draw it with an explicit destination size; it is only ever downscaled slightly (≤ one bucket).
 */
export function brandRaster(path: string, devicePx: number): CanvasImageSource | null {
  const img = brandImage(path);
  if (!img) return null;
  const px = Math.max(BUCKET, Math.min(1024, Math.ceil(devicePx / BUCKET) * BUCKET));
  const key = `${path}@${px}`;
  let c = rasters.get(key);
  if (!c) {
    c = document.createElement('canvas');
    c.width = px;
    c.height = px;
    const g = c.getContext('2d');
    if (!g) return null;
    g.imageSmoothingQuality = 'high';
    g.drawImage(img, 0, 0, px, px);
    if (rasters.size > 160) rasters.clear(); // viewport changes: just rebuild what is still needed
    rasters.set(key, c);
  }
  return c;
}

/** Current device-pixel scale of a context (the renderer applies DPR as its base transform). */
export function deviceScale(ctx: CanvasRenderingContext2D): number {
  const t = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
  return t ? Math.hypot(t.a, t.b) || 1 : 1;
}

// ---- runner sprite atlases -------------------------------------------------------------------------------------

/** Sprite states shipped by the pack (`brand/exports/sprites/runner-<state>.json`). */
export type SpriteState =
  | 'idle'
  | 'start'
  | 'run-forward'
  | 'steer-left'
  | 'steer-right'
  | 'counter-steer'
  | 'stumble'
  | 'overheat'
  | 'cool-recover'
  | 'shoe-pickup'
  | 'finish'
  | 'waiting';

export const SPRITE_STATES: SpriteState[] = ['run-forward', 'idle', 'waiting', 'steer-left', 'steer-right', 'counter-steer', 'start', 'stumble', 'overheat', 'cool-recover', 'shoe-pickup', 'finish'];
export const EFFECT_STATES: ReadonlySet<SpriteState> = new Set(['stumble', 'overheat', 'cool-recover', 'shoe-pickup', 'finish']);

export interface SpriteMeta {
  loop: boolean;
  durationMs: number;
  /** Physical rects in the contrast atlas, [sx, sy, sw, sh]. */
  rects: [number, number, number, number][];
  /** Logical frame size (256). */
  size: number;
  groundReference: [number, number];
  atlas: string;
}

interface RawMeta {
  loop: boolean;
  durationMs: number;
  width: number;
  groundReference: [number, number];
  contrastAtlas: string;
  contrastFrames?: { rect: [number, number, number, number] }[];
  frames: { rect: [number, number, number, number] }[];
}

const metas = new Map<SpriteState, SpriteMeta | 'loading' | 'failed'>();

function loadMeta(state: SpriteState) {
  if (metas.has(state) || !canLoad || typeof fetch !== 'function') return;
  metas.set(state, 'loading');
  fetch(`${BASE}sprites/runner-${state}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<RawMeta>) : Promise.reject(new Error(String(r.status)))))
    .then((m) => {
      const frames = m.contrastFrames ?? m.frames;
      metas.set(state, { loop: m.loop, durationMs: m.durationMs, rects: frames.map((f) => f.rect), size: m.width, groundReference: m.groundReference, atlas: m.contrastAtlas });
      brandImage(`sprites/${m.contrastAtlas}`);
    })
    .catch(() => metas.set(state, 'failed'));
}

/** Kick off loading of every runner state (metadata, contrast atlas, effect overlays, shadow). Idempotent. */
export function preloadRunnerArt() {
  for (const s of SPRITE_STATES) loadMeta(s);
  for (const s of EFFECT_STATES) brandImage(`sprites/runner-${s}-effect-atlas.png`);
  brandImage('sprites/runner-shadow.png');
}

/** Metadata + decoded atlas for a state, or null while either is still loading. */
export function runnerSprite(state: SpriteState): { meta: SpriteMeta; atlas: HTMLImageElement } | null {
  const m = metas.get(state);
  if (!m) loadMeta(state);
  if (!m || typeof m === 'string') return null;
  const atlas = brandImage(`sprites/${m.atlas}`);
  return atlas ? { meta: m, atlas } : null;
}

export function runnerEffect(state: SpriteState): HTMLImageElement | null {
  return EFFECT_STATES.has(state) ? brandImage(`sprites/runner-${state}-effect-atlas.png`) : null;
}
